import { yupResolver } from '@hookform/resolvers/yup'
import { Checkbox, Button, Grid, Alert } from '@mantine/core'
import { Form, useForm } from 'react-hook-form'
import { TextInput, DateInput, NativeSelect, NumberInput } from 'react-hook-form-mantine'
import yup from '../lib/yup'
import { useState, useEffect } from 'react'
import useApi from '../hooks/useApi'
import haxios from '../lib/haxios'
import moment from 'moment'
import { errorToast, successToast } from '../lib/helpers'
import { modals } from '@mantine/modals'

/*
{
  FName: String,
  SName: String,
  Dob: Date,
  Branch: String, // MamCode
  BranchName: String,
  Position: String,
  AllowLogin: Boolean,
  Email: String,
  Username: String,
  Password: String,
  IsAdmin: Boolean,
  Inits: String,
  SalesTarget: Number,
  Status: String,
  HolidayStartDate: String, // MM-DD
  HolidayAllocation: Number
}
*/

const validation = yup.object().shape({
  FName: yup
    .string()
    .required('This field is required')
    .matches(/^[a-zA-Z0-9\s]+$/, 'Only alphanumeric characters are allowed')
    .max(50, 'Must be shorter than 50 characters'),
  SName: yup
    .string()
    .required('This field is required')
    .matches(/^[a-zA-Z0-9\s]+$/, 'Only alphanumeric characters are allowed')
    .max(50, 'Must be shorter than 50 characters'),
  Dob: yup.date().required('This field is required').max(new Date(), 'Date of birth cannot be in the future'),
  Branch: yup
    .string()
    .matches(/^[a-zA-Z0-9]+$/, 'Only alphanumeric characters are allowed')
    .required('This field is required'),
  Position: yup
    .string()
    .required('This field is required')
    .matches(/^[a-zA-Z0-9\s]+$/, 'Only alphanumeric characters are allowed')
    .max(50, 'Must be shorter than 50 characters'),
  AllowLogin: yup.boolean().required('This field is required'),
  Email: yup
    .string()
    .email('Invalid email format')
    .when('AllowLogin', {
      is: true,
      then: (schema) => schema.required('Email is required when login is allowed'),
      otherwise: (schema) => schema.notRequired()
    }),
  Username: yup
    .string()
    .when('AllowLogin', {
      is: true,
      then: (schema) => schema.required('Username is required when login is allowed'),
      otherwise: (schema) => schema.notRequired()
    })
    .min(3, 'Username must be at least 3 characters')
    .matches(/^[a-zA-Z0-9_\.]+$/, 'Only alphanumeric characters, underscore and full stop allowed'),
  Password: yup
    .string()
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]+$/,
      'Password must contain at least one letter, one number, and one special character'
    )
    .when('AllowLogin', {
      is: true,
      then: (schema) => schema.required('Password is required when login is allowed'),
      otherwise: (schema) => schema.notRequired()
    }),
  IsAdmin: yup.boolean().required('This field is required'),
  Inits: yup
    .string()
    .required('This field is required')
    .matches(/^[A-Z]+$/, 'Only uppercase letters allowed')
    .max(5, 'Must be 5 characters or less'),
  SalesTarget: yup.number().emptyStringToZero().nullable().min(0, 'Sales target cannot be negative'),
  StaffCategory: yup.string().required('Staff category is required'),
  MonthlyCostPerHead: yup.number().nullable().min(0, 'Monthly cost cannot be negative'),
  //Status: yup.string().required('This field is required'),
  HolidayStartDate: yup
    .string()
    .matches(/^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, 'Date must be in MM-DD format')
    .required('This field is required'),
  HolidayAllocation: yup
    .number()
    .integer('Must be a whole number')
    .min(0, 'Holiday allocation cannot be negative')
    .required('This field is required')
})

const StaffEditModal = ({ context, id, innerProps }) => {
  const [showKeyCode, setShowKeyCode] = useState(false)
  const [generatedPassword, setGeneratedPassword] = useState('')

  const { control, watch, setValue } = useForm({
    resolver: yupResolver(validation),
    defaultValues: innerProps.defaultValues || {
      FName: '',
      SName: '',
      Dob: null,
      Position: '',
      Branch: null,
      AllowLogin: false,
      Email: '',
      Username: '',
      Password: '',
      IsAdmin: false,
      Inits: '',
      SalesTarget: null,
      StaffCategory: '',
      MonthlyCostPerHead: null,
      //Status: 'Active',
      HolidayStartDate: '01-01',
      HolidayAllocation: 20,
      KeyCode: null
    }
  })

  const allowLogin = watch('AllowLogin')
  const fName = watch('FName')
  const sName = watch('SName')

  const generatePassword = () => {
    const randomWord = Math.random().toString(36).substring(2, 10)
    const capitalizedWord = randomWord.charAt(0).toUpperCase() + randomWord.slice(1)
    const randomNumber = Math.floor(1000 + Math.random() * 9000)
    return `${capitalizedWord}${randomNumber}@`
  }

  // Generate credentials when name fields change
  useEffect(() => {
    if (fName && sName) {
      const sanitizedFName = fName.toLowerCase().replace(/[^a-z0-9]/g, '')
      const sanitizedSName = sName.toLowerCase().replace(/[^a-z0-9]/g, '')

      const username = `${sanitizedFName}.${sanitizedSName}${Math.floor(Math.random() * 9000) + 999}`
      setValue('Username', username)

      const email = `${username}@automm.co.uk`
      setValue('Email', email)
    } else {
      setValue('Username', '')
      setValue('Email', '')
    }
  }, [fName, sName, setValue])

  // Generate and set password on initial load
  useEffect(() => {
    const password = generatePassword()
    setGeneratedPassword(password)
    setValue('Password', password)
  }, [setValue])

  const handleSubmit = async (data) => {
    try {
      const formattedData = {
        ...data,
        Dob: moment(data.Dob).format('YYYY-MM-DD'),
        Inits: data.Inits.toUpperCase()
      }

      await innerProps.submit(formattedData)
      await innerProps.refetch()
      context.closeModal(id)

      if (data.AllowLogin) {
        modals.openContextModal({
          modal: 'upwModal',
          withCloseButton: false,
          centered: true,
          innerProps: {
            message: `The username is ${data.Username} and the password is ${data.Password}. Have you noted these down?`
          }
        })
      }

      successToast('Staff member added.')
    } catch (e) {
      errorToast('An error has occurred.')
      console.log(e)
    }
  }

  // Generate arrays for month and day dropdowns
  const months = Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1).padStart(2, '0'),
    label: moment().month(i).format('MMMM')
  }))

  const days = Array.from({ length: 31 }, (_, i) => ({
    value: String(i + 1).padStart(2, '0'),
    label: String(i + 1)
  }))

  return (
    <Form control={control} onSubmit={(e) => handleSubmit(e.data)} onError={(e) => console.log(e)}>
      <Grid gutter="xs">
        <Grid.Col span={6}>
          <TextInput label="Forename" name="FName" control={control} />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput label="Surname" name="SName" control={control} />
        </Grid.Col>

        <Grid.Col span={6}>
          <DateInput label="Date of Birth" name="Dob" control={control} />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput label="Inits" name="Inits" control={control} transform={(value) => value.toUpperCase()} />
        </Grid.Col>

        <Grid.Col span={6}>
          <BranchSelect label="Main branch" name="Branch" control={control} />
        </Grid.Col>
        {/* <Grid.Col span={6}>
          <NativeSelect label="Status" name="Status" control={control} data={['Active', 'Inactive', 'On Leave']} />
        </Grid.Col> */}

        <PositionSelect label="Position" name="Position" control={control} />

        <Grid.Col span={6}>
          <NativeSelect 
            label="Staff Category" 
            name="StaffCategory" 
            control={control} 
            data={[
              { value: '', label: 'Select a category' },
              { value: 'Biker', label: 'Biker' },
              { value: 'Warehouse', label: 'Warehouse' },
              { value: 'Sales Staff', label: 'Sales Staff' },
              { value: 'Manager', label: 'Manager' },
              { value: 'Admin', label: 'Admin' },
              { value: 'Other', label: 'Other' }
            ]} 
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <NumberInput label="Monthly Cost Per Head (£)" name="MonthlyCostPerHead" control={control} min={0} precision={2} />
        </Grid.Col>

        <Grid.Col span={6}>
          <NumberInput label="Sales Target" name="SalesTarget" control={control} min={0} />
        </Grid.Col>
        <Grid.Col span={6}>
          <NumberInput label="Holiday Allocation" name="HolidayAllocation" control={control} min={0} />
        </Grid.Col>

        <Grid.Col span={12}>
          <div className="flex space-x-4">
            <NativeSelect
              label="Holiday Start Month"
              name="holidayMonth"
              control={control}
              data={months}
              style={{ width: '50%' }}
              onChange={(value) => {
                const day = watch('holidayDay') || '01'
                setValue('HolidayStartDate', `${value}-${day}`)
              }}
            />
            <NativeSelect
              label="Holiday Start Day"
              name="holidayDay"
              control={control}
              data={days}
              style={{ width: '50%' }}
              onChange={(value) => {
                const month = watch('holidayMonth') || '01'
                setValue('HolidayStartDate', `${month}-${value}`)
              }}
            />
          </div>
        </Grid.Col>

        <Grid.Col span={12}>
          <Checkbox label="Allow Login" {...control.register('AllowLogin')} />
        </Grid.Col>

        {allowLogin && (
          <>
            <Grid.Col span={12}>
              <Alert color="yellow">
                We have generated a password for you. This will not be displayed again so please make a note of it.
              </Alert>
            </Grid.Col>
            {/* <Grid.Col span={12}>
              <TextInput label="Email" name="Email" control={control} type="email" />
            </Grid.Col> */}
            <Grid.Col span={6}>
              <TextInput label="Username" name="Username" control={control} disabled={true} />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label="Password" name="Password" control={control} disabled={true} />
            </Grid.Col>
            <Grid.Col span={12}>
              <Checkbox label="Is Admin" name="IsAdmin" control={control} />
            </Grid.Col>
          </>
        )}

        {/* <Grid.Col span={12}>
          <Checkbox label="Autopart user?" checked={showKeyCode} onChange={(e) => setShowKeyCode(e.target.checked)} />
        </Grid.Col>

        {showKeyCode && (
          <Grid.Col span={12}>
            <AutopartUserSelect name="KeyCode" control={control} />
          </Grid.Col>
        )} */}

        <Grid.Col span={12}>
          <Button type="submit">Submit</Button>
        </Grid.Col>
      </Grid>
    </Form>
  )
}

const BranchSelect = ({ label, name, control }) => {
  const { data, isLoading } = useApi({ url: 'https://api.aa-vision.com/api/map/branches/' })
  const options = [
    { value: '', label: 'Select a branch' },
    ...(isLoading ? [] : data.map((el) => ({ value: el.Branch, label: el.Branch })))
  ]

  return <NativeSelect label={label} name={name} control={control} data={options} placeholder="Select a branch" />
}

const PositionSelect = ({ label, name, control }) => {
  const [freeInput, setFreeInput] = useState(false)
  const { data, isLoading } = useApi({ url: '/salesassistnew/positions' })

  if (freeInput) {
    return (
      <Grid.Col span={12}>
        <div className="flex items-end space-x-2">
          <TextInput label={label} name={name} control={control} style={{ width: 200 }} placeholder="Enter position" />
          <Button variant="outline" onClick={() => setFreeInput(false)} className="w-[96px]">
            Cancel
          </Button>
        </div>
      </Grid.Col>
    )
  }

  const options = [
    { value: '', label: 'Select a position' },
    ...(isLoading ? [] : data.map((pos) => ({ value: pos, label: pos })))
  ]

  return (
    <Grid.Col span={12}>
      <div className="flex items-end space-x-2">
        <NativeSelect
          label={label}
          name={name}
          control={control}
          data={options}
          style={{ width: 200 }}
          placeholder="Select a position"
        />
        <Button onClick={() => setFreeInput(true)} className="w-[96px]">
          Add new
        </Button>
      </div>
    </Grid.Col>
  )
}

const AutopartUserSelect = ({ label, name, control }) => {
  const { data, isLoading } = useApi({ url: 'https://api.aa-vision.com/api/map/codes/operators/' })

  const options = (isLoading ? [] : data).map((el) => ({
    label: `${el.A1} - ${el.KeyCode}`,
    value: el.KeyCode
  }))

  return <NativeSelect label={label} name={name} control={control} data={options} />
}

export default StaffEditModal
